/**
 * Pure helpers for the Employee Configuration page.
 * Keep numbering preview and filters frontend-only until the user saves.
 */

import {
  DEFAULT_EMPLOYEE_TYPE,
  EMPLOYEE_TYPES,
} from "../../Moduels/Employees/employeeTypeConstants";

export const DEFAULT_NUMBERING = {
  auto_generate: true,
  use_prefix: true,
  prefix: "EMP",
  suffix: "",
  starting_number: 1,
  number_length: 4,
};

export const EMP_CONFIG_PATH = "/settings/employee-configuration";
export const EMP_CONFIG_BACK_PATH = "/settings/statutory";
export const EMP_CONFIG_NEXT_PATH = "/employees/add";

export function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `et_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function seedFromConstants() {
  return EMPLOYEE_TYPES.map((name) => ({
    id: newId(),
    name,
    description:
      name === "Permanent"
        ? "Employee on permanent employment"
        : name === "Contract"
        ? "Employee hired on contract basis"
        : name === "Probation"
        ? "Employee under probation period"
        : name === "Intern"
        ? "Employee hired for internship or training"
        : "",
    is_system: true,
    is_default: name === DEFAULT_EMPLOYEE_TYPE,
    is_active: true,
  }));
}

export function normalizeList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => ({
      id: row?.id || newId(),
      name: String(row?.name || "").trim(),
      description: String(row?.description || "").trim(),
      is_system: Boolean(row?.is_system),
      is_default: Boolean(row?.is_default),
      is_active: row?.is_active !== false,
    }))
    .filter((row) => row.name);
}

export function emptyEtForm() {
  return { name: "", description: "" };
}

export function validateEtForm(form, existing, editingId) {
  const errors = {};
  const name = (form.name || "").trim();
  if (!name) errors.name = "Employee type name is required";
  else if (name.length > 64) errors.name = "Name must be 64 characters or less";
  const dup = (existing || []).some(
    (row) =>
      (!editingId || row.id !== editingId) &&
      String(row.name || "").toLowerCase() === name.toLowerCase()
  );
  if (dup) errors.name = "An employee type with this name already exists";
  if ((form.description || "").length > 250)
    errors.description = "Description must be 250 characters or less";
  return errors;
}

export function buildPreviewCodes(
  prefix,
  suffix,
  startNum,
  length,
  count = 4,
  usePrefix = true
) {
  const pfx = usePrefix
    ? ((prefix || "EMP").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4) || "EMP")
    : "";
  const sfx = (suffix || "").toUpperCase();
  const start = Math.max(1, parseInt(startNum, 10) || 1);
  const len = Math.max(1, Math.min(9, parseInt(length, 10) || 4));
  return Array.from({ length: count }, (_, i) =>
    `${pfx}${String(start + i).padStart(len, "0")}${sfx}`
  );
}

export function formatNumberingExample(numbering = DEFAULT_NUMBERING) {
  return buildPreviewCodes(
    numbering.prefix,
    numbering.suffix,
    numbering.starting_number,
    numbering.number_length,
    1,
    numbering.use_prefix !== false
  )[0];
}

export function numberingSnapshot(n = DEFAULT_NUMBERING) {
  return JSON.stringify({
    auto_generate: Boolean(n.auto_generate),
    use_prefix: n.use_prefix !== false,
    prefix: String(n.prefix || "").toUpperCase(),
    suffix: String(n.suffix || "").toUpperCase(),
    starting_number: Math.max(1, parseInt(n.starting_number, 10) || 1),
    number_length: Math.max(1, Math.min(9, parseInt(n.number_length, 10) || 4)),
  });
}

export function isNumberingDirty(current, saved) {
  return numberingSnapshot(current) !== numberingSnapshot(saved);
}

export function numberingPersistedFromHr(hr) {
  if (!hr || typeof hr !== "object") return false;
  if (hr.employee_numbering && typeof hr.employee_numbering === "object") return true;
  if (typeof hr.employee_prefix === "string" && hr.employee_prefix.length > 0) return true;
  if (typeof hr.employee_auto_increment === "boolean") return true;
  if (hr.employee_number_start != null) return true;
  if (typeof hr.employee_use_prefix === "boolean") return true;
  return false;
}

export function isTypesConfigured(types) {
  return Array.isArray(types) && types.length > 0;
}

export function isNumberingConfigured(numbering, persisted) {
  if (!persisted) return false;
  if (!numbering) return false;
  if (numbering.auto_generate && numbering.use_prefix !== false) {
    const letters = String(numbering.prefix || "")
      .replace(/[^A-Za-z]/g, "")
      .toUpperCase();
    if (!letters) return false;
  }
  return true;
}

export function getConfigCompletion({ typesConfigured, numberingConfigured }) {
  const done = [Boolean(typesConfigured), Boolean(numberingConfigured)].filter(Boolean).length;
  if (done === 2) {
    return {
      complete: true,
      sectionsDone: 2,
      title: "Employee Configuration Complete",
      detail: "Employee Types and Employee Numbering are configured",
    };
  }
  if (done === 0) {
    return {
      complete: false,
      sectionsDone: 0,
      title: "Setup required",
      detail: "Configure employee types and employee numbering",
    };
  }
  return {
    complete: false,
    sectionsDone: done,
    title: `${done} of 2 sections configured`,
    detail: typesConfigured
      ? "Employee Numbering still needs setup"
      : "Employee Types still need setup",
  };
}

export function filterEmployeeTypes(types, { search = "", status = "all" } = {}) {
  const q = String(search || "").trim().toLowerCase();
  return (types || []).filter((row) => {
    if (status === "active" && !row.is_active) return false;
    if (status === "inactive" && row.is_active) return false;
    if (!q) return true;
    const name = String(row.name || "").toLowerCase();
    const description = String(row.description || "").toLowerCase();
    return name.includes(q) || description.includes(q);
  });
}

export function hashToSection(hash) {
  if (hash === "#employee-numbering") return "employee-numbering";
  if (hash === "#employee-types" || hash === "#employment-types") return "employee-types";
  return null;
}

export function countEmployeesByType(employees) {
  const counts = {};
  for (const emp of employees || []) {
    const key = String(emp.employee_type || "").trim().toLowerCase();
    if (!key) continue;
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

export function lookupTypeCount(counts, typeName) {
  if (!counts || !typeName) return 0;
  return counts[String(typeName).trim().toLowerCase()] || 0;
}

export function parseEmployeeListPayload(data) {
  if (Array.isArray(data)) return { items: data, total: data.length };
  const items = Array.isArray(data?.items) ? data.items : [];
  const total = Number(data?.total ?? items.length) || items.length;
  return { items, total };
}
